import { useCallback, useEffect, useReducer, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import userpool from "../Config/userpool";

const TOKEN_KEY = "token";

/*
 * The router used to treat any non-empty string in localStorage as a session.
 * A token left behind by a previous visit is a non-empty string, so the
 * dashboard rendered, every request came back 403, and the only way out was
 * clearing site data by hand. Read the expiry the token carries instead.
 *
 * Cognito ID tokens last an hour, so simply signing people out on expiry would
 * mean logging in again every hour. The refresh token sitting beside it is good
 * for far longer, so a stale token is a reason to renew, not to sign out.
 */
const RENEW_BEFORE_EXPIRY_MS = 60 * 1000;
const RENEW_INTERVAL_MS = 60 * 1000;
const REFRESH_TIMEOUT_MS = 8 * 1000;

// * When the token expires, in ms, or null if it isn't a JWT with an expiry. * //
const readExpiry = (token) => {
  try {
    const { exp } = jwtDecode(token);
    return typeof exp === "number" ? exp * 1000 : null;
  } catch (err) {
    // * Not a JWT at all - hand-edited, truncated, or from an older release. * //
    return null;
  }
};

// * The stored token if it can still be sent, otherwise null. * //
export const readValidToken = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return null;
  const expiresAt = readExpiry(token);
  return expiresAt && expiresAt > Date.now() ? token : null;
};

export const signOut = () => {
  localStorage.clear();
};

export const refreshSession = () =>
  new Promise((resolve, reject) => {
    const user = userpool.getCurrentUser();
    if (!user) {
      reject(new Error("No Cognito session to renew"));
      return;
    }

    /*
     * getSession has no timeout of its own. The router holds the first paint
     * until this settles, so a request that never comes back would leave a
     * blank page - worse than the broken dashboard this replaced. Give up
     * after a few seconds and treat it as signed out.
     */
    let done = false;
    const finish = (fn, value) => {
      if (done) return;
      done = true;
      fn(value);
    };
    const timer = setTimeout(
      () => finish(reject, new Error("Timed out renewing the Cognito session")),
      REFRESH_TIMEOUT_MS
    );

    user.getSession((err, session) => {
      clearTimeout(timer);
      if (err || !session || !session.isValid()) {
        finish(reject, err || new Error("Cognito session is no longer valid"));
        return;
      }
      // * Signed out while this was in flight - don't sign them back in. * //
      if (!localStorage.getItem(TOKEN_KEY)) {
        finish(reject, new Error("Signed out while renewing"));
        return;
      }
      const jwt = session.getIdToken().getJwtToken();
      localStorage.setItem(TOKEN_KEY, jwt);
      finish(resolve, jwt);
    });
  });

/*
 * Returns the token the app may use, and whether that is decided yet. While
 * settled is false the router renders nothing: an expired token may only need
 * renewing, and must not bounce someone to the login page before we've tried.
 *
 * Storage is read on every render rather than held in state. Login and logout
 * write localStorage and then navigate without a reload, and that navigation
 * is what re-renders the router. Holding the answer in state meant a fresh
 * login still read as logged out - straight back to the login page - and a
 * logout still read as logged in, until the next timer tick a minute later.
 */
export const useSessionToken = () => {
  const [, rerender] = useReducer((count) => count + 1, 0);
  const renewing = useRef(false);
  const alive = useRef(true);

  const token = readValidToken();
  const settled = Boolean(token) || !localStorage.getItem(TOKEN_KEY);

  // * Renews a token that has expired or will within the minute, so a request
  // * is never sent on one that expires in flight. Only a refusal or timeout
  // * from Cognito signs the user out. * //
  const renewIfDue = useCallback(() => {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (!stored || renewing.current) return;
    const expiresAt = readExpiry(stored);
    if (expiresAt && expiresAt - RENEW_BEFORE_EXPIRY_MS > Date.now()) return;

    renewing.current = true;
    refreshSession()
      .catch(() => signOut())
      .finally(() => {
        renewing.current = false;
        if (alive.current) rerender();
      });
  }, []);

  // * After every render too, so a token that expired while the tab sat idle
  // * is renewed on the next navigation rather than up to a minute later. * //
  useEffect(() => {
    renewIfDue();
  });

  useEffect(() => {
    alive.current = true;
    const timer = setInterval(renewIfDue, RENEW_INTERVAL_MS);
    return () => {
      alive.current = false;
      clearInterval(timer);
    };
  }, [renewIfDue]);

  return { token, settled };
};
